import Project, {ProjectDef, ProjectFile, ProjectId} from '@/types/Project';
import {ActionContext, Module} from 'vuex';

import emulatorVersions from '../emulatorVersions';
// tslint:disable:no-var-requires

import RootState from '@/store/rootState';
import remote from '@/remote';

export interface ProjectState {
    dirty: boolean;
    projectDef?: ProjectDef;
    files?: ProjectFile[];
}

let retrolabDef: string | undefined;

export const defaultProject = `start:
\tjmp start`;

type ProjectContext = ActionContext<ProjectState, RootState>;

async function loadRetrolabDef() : Promise<void> {
    // load retrolab.def
    if (retrolabDef === undefined) {
        if (typeof fetch === 'undefined')  // running under node
            retrolabDef = ';';
        else {
            const response = await fetch(`/retrolab-${emulatorVersions[0]}.def`);
            retrolabDef = await response.text();
        }
    }
}

const project: Module<ProjectState, RootState> = {
    namespaced: true,

    state: {
        dirty: false,
    },

    actions: {
        //
        // creation / destruction
        //
        async create({ commit, state, rootState }: ProjectContext, newProject: Project): Promise<ProjectId> {
            await loadRetrolabDef();
            // create project
            if (newProject.name.length < 3) {
                throw new Error('Project name must have at least 3 characters.');
            }
            const uid = rootState.user.user!.uid;
            newProject.uid = uid;
            newProject.username = rootState.user.user!.username!;
            if (newProject.archVersion === 'latest')
                newProject.archVersion = emulatorVersions[0];
            const projectId = await remote().createProject(newProject);
            commit('CREATE_PROJECT', { id: projectId, project: newProject } as ProjectDef);
            await Promise.all(state.files!.map((f: ProjectFile) => remote().saveFile(uid, projectId, f))!);
            return projectId;
        },

        async delete({ rootState, dispatch }: ProjectContext, projectId: ProjectId): Promise<void> {
            const submissionId = (await remote().loadProject(projectId)).submissionId;
            const threads : Array<Promise<void>> = [remote().deleteProject(projectId)];
            if (submissionId!) {
                threads.push(dispatch('submissions/deleteSubmission', submissionId!, { root: true }));
            }
            await Promise.all(threads);
        },

        async close({ commit, dispatch }: ProjectContext) : Promise<void> {
            try {
                await dispatch('autosave/autosave', undefined, { root: true });
                // tslint:disable-next-line:no-empty
            } catch (_) {}
            commit('CLOSE');
        },


        //
        // loading
        //
        async load({ commit, rootState }: ProjectContext, projectId: ProjectId): Promise<void> {
            const [loadedProject, files] = await Promise.all([
                remote().loadProject(projectId),
                remote().loadAllFiles(rootState.user.user!.uid, projectId),
            ]);
            if (files.length === 0) {
                throw new Error('This project has no files - this is a bug and should never happen.');
            }
            commit('SET_PROJECT_DEF', { id: projectId, project: loadedProject } as ProjectDef);
            commit('SET_PROJECT_FILES', files);
            commit('CLEAR_DIRTY_FLAGS');
        },

        //
        // update
        //

        async upgradeToLatest({ dispatch, state, commit }: ProjectContext) {
            await loadRetrolabDef();
            const promises = [];
            // upgrade version in project
            const projectDef = { ...state.projectDef! } as ProjectDef;
            projectDef.project.archVersion = emulatorVersions[0];
            promises.push(await dispatch('update', projectDef));
            // replace retrolab.def
            console.log(retrolabDef);
            commit('SAVE_FILE', { name: 'retrolab.def', content: retrolabDef } as ProjectFile);
            promises.push(await dispatch('updateFiles'));
            await Promise.all(promises);
        },

        async update({ commit }: ProjectContext, projectDef: ProjectDef) {
            await remote().updateProject(projectDef);
            commit('SET_PROJECT_DEF', projectDef);
        },

        async updateFiles({ commit, state, rootState }: ProjectContext) {
            const uid: UID = rootState.user.user!.uid;
            const projectId: ProjectId = state.projectDef!.id;
            const filesToUpdate = state.files!.filter((f) => f.dirty);
            const localObjects = state.files!.map((f) => f.name);
            const remoteObjects = await remote().listFiles(uid, projectId);
            const filesToDelete = remoteObjects.filter((f) => !localObjects.includes(f));
            await Promise.all([
                remote().updateFiles(uid, projectId, filesToUpdate),
                remote().deleteFiles(uid, projectId, filesToDelete),
            ]);
            commit('CLEAR_DIRTY_FLAGS');
        },

    },

    mutations: {
        //
        // project
        //
        CREATE_PROJECT(state: ProjectState, projectDef: ProjectDef): void {
            state.projectDef = projectDef;
            state.files = [
                { name: 'retrolab.def', content: retrolabDef!, dirty: true },
                { name: 'main.s', content: defaultProject, dirty: true },
            ];
        },

        SET_PROJECT_DEF(state: ProjectState, projectDef: ProjectDef): void {
            state.projectDef = projectDef;
        },

        SET_PROJECT_FILES(state: ProjectState, files: ProjectFile[]): void {
            state.files = files;
        },

        CLEAR_DIRTY_FLAGS(state: ProjectState) {
            for (const f of state.files!) {
                f.dirty = false;
            }
            state.dirty = false;
        },

        CLOSE(state: ProjectState) {
            state.dirty = false;
            delete state.projectDef;
            delete state.files;
        },


        //
        // files
        //
        CREATE_FILE(state: ProjectState, newFile: ProjectFile): void {
            const file = state.files!.find((f) => f.name === newFile.name);
            if (file) {
                throw new Error('This file already exists on the project.');
            } else {
                state.files = [...state.files!, { ...newFile, dirty: true } as ProjectFile ];
            }
            state.dirty = true;
        },

        SAVE_FILE(state: ProjectState, fileToSave: ProjectFile) {
            const file = state.files!.find((f) => f.name === fileToSave.name);
            if (file) {
                if (fileToSave.content === file.content) {
                    return;
                }
                file.content = fileToSave.content;
                file.dirty = true;
            } else {
                state.files = [...state.files!, { ...fileToSave, dirty: true } as ProjectFile];
            }
            state.dirty = true;
        },

        DELETE_FILE(state: ProjectState, filename: string): void {
            if (filename === 'main.s' || filename === 'retrolab.def') {
                throw new Error(`This default file can't be removed from the project.`);
            } else {
                state.files = state.files!.filter((f) => f.name !== filename);
            }
        },
    },
};

export default project;
